<style lang="scss" scoped>
.slideout-container {
    width: 100vw;
    height: 100vh;
    position: fixed;
    pointer-events: none;
    top: 0;
    left: 0;
    z-index: 10000;
    transition: background-color 0.15s;

    .slideout {
        $slideoutWidth: 400px;
        position: fixed;
        display: flex;
        flex-direction: column;
        width: $slideoutWidth;
        right: -$slideoutWidth;
        height: 100%;
        opacity: 0;
        top: 0;
        background: #fff;
        box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.16);
        transition: right 0.15s ease-in-out, opacity 0.15s ease-in-out;
        overflow-y: auto;
        overflow-x: hidden;

        @media screen and (max-width: 500px) {
            width: 100%;
            right: -100%;
        }

        .close {
            position: absolute;
            width: 12px;
            height: 12px;
            right: 26px;
            top: 26px;
            cursor: pointer;

            &:before,
            &:after {
                content: "";
                position: absolute;
                width: 14px;
                height: 1px;
                border-radius: 1px;
                left: 0;
                transform-origin: left;
                transition: background 0.15s ease-in-out;
                background-color: #000;
            }

            &:before {
                top: 1px;
                transform: rotate(45deg);
            }

            &:after {
                bottom: 0;
                transform: rotate(-45deg);
            }

            &:hover {
                &:before,
                &:after {
                    background-color: #000;
                }
            }
        }

        .slideout-content {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
        }
    }

    &.active {
        pointer-events: all;
        background-color: rgba(27, 28, 31, 0.24);

        .slideout {
            right: 0;
            opacity: 1;
        }
    }
}

.component-fade-enter-active,
.component-fade-leave-active {
    transition: opacity 0.3s ease;
}
.component-fade-enter,
.component-fade-leave-to {
    opacity: 0;
}
</style>

<template>
    <div class="reviews">
        <div class="lg:mb-60 md:flex-row md:items-center lg:pb-24 flex flex-col items-start justify-between pb-32 mb-32 border-b border-gray-300" v-if="showHeader">
            <div class="md:mb-0 flex flex-col mb-24">
                <h2 class="h4 lg:mb-8 mb-24">Reviews</h2>
                <score-stars class="lg:mb-16 mb-8" :score="averageScore" :large="true"></score-stars>
                <p v-html="`${averageScore} sterren op basis van ${reviews.length} reviews`"></p>
            </div>
            <button class="btn btn-green" @click="toggleSlideout">{{ translations.write_a_review }}</button>
        </div>

        <div class="lg:-mb-60 -mb-32">
            <transition-group name="component-fade" mode="out-in" tag="div" class="row gutter-60">
                <div class="lg:mb-60 mb-32" :class="columnClass" v-for="review in loadedReviews" :key="review.date">
                    <div class="review flex flex-col items-start">
                        <p v-html="review.title" class="text-20 font-bold text-gray-800"></p>
                        <time class="text-14 text-blue opacity-70 mb-8" :datetime="review.date" v-html="formattedDate(review.date)"></time>
                        <score-stars class="mb-16" :score="review.score"></score-stars>
                        <p v-html="review.text" class="text-black"></p>
                    </div>
                </div>
            </transition-group>
        </div>

        <div class="flex justify-center mt-40" v-if="canLoadMore">
            <button class="btn btn-outline-blue" @click="loadMore">{{ translations.show_more }}</button>
        </div>

        <div class="slideout-container" @click="toggleSlideout" :class="{active: slideoutIsOpen}" v-if="showHeader">
            <div class="slideout" @click.stop>
                <div class="close" @click="toggleSlideout"></div>
                <div class="slideout-content">
                    <div class="md:py-80 md:px-40 px-20 py-56">
                        <el-form button-class="btn btn-green btn-rounded w-full font-bold" submit-text="Review plaatsen">
                            <h2 class="h5 mb-16">{{ translations.write_a_review }}</h2>
                            <score-stars @score-stars-input="setScore" :score="5" :large="true" :input="true" class="md:mb-40 mb-24"></score-stars>
                            <el-input v-model="newReview.title" rules="required" :label="translations.title_label" class="mb-16"></el-input>
                            <el-input v-model="newReview.text" rules="required" :label="translations.description" type="textarea" class="mb-16"></el-input>
                        </el-form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import dayjs from 'dayjs'
const nl_BE = require('dayjs/locale/nl-be')

export default {
    name: "Ui.Reviews",

    props: {
        reviews: {
            type: Array,
            required: true
        },

        showHeader: {
            type: Boolean,
            required: false,
            default: true
        },

        translations: {
            type: Object,
            required: true,
        },

        columnClass: {
            type: String,
            required: false,
            default: () => {
                return 'w-full lg:w-1/2'
            }
        }
    },

    data() {
        return {
            loadedReviews: [],
            slideoutIsOpen: false,

            newReview: {
                score: 5,
                title: '',
                text: ''
            }
        }
    },

    mounted() {
        this.loadMore();
    },

    methods: {
        toggleSlideout() {
            this.slideoutIsOpen = !this.slideoutIsOpen;
        },

        loadMore() {
            if (this.canLoadMore) {
                let loadSize = this.$root.screen.lg ? 4 : 2;
                let arraySizeDiff = this.reviews.length - this.loadedReviews.length;
                let spliceSize = arraySizeDiff > loadSize ? loadSize : arraySizeDiff;
                let nextReviews = this.reviews.slice(this.loadedReviews.length, this.loadedReviews.length + spliceSize);
                this.loadedReviews.push(...nextReviews);
            }
        },

        roundHalf(num) {
            return Math.round(num * 2) / 2;
        },

        formattedDate(date) {
            return dayjs(date).locale(nl_BE).format('D MMMM YYYY');
        },

        setScore(score) {
            this.newReview.score = score;
        }
    },

    computed: {
        canLoadMore() {
            return this.reviews.length > this.loadedReviews.length;
        },

        averageScore() {
            let totalScore = 0;
            let amountOfReviews = this.reviews.length;

            for (let index = 0; index < this.reviews.length; index++) {
                totalScore += this.reviews[index].score;
            }

            return this.roundHalf(totalScore / amountOfReviews);
        },
    }
}
</script>